<template>
  <div>
    <!-- Botón flotante de la burbuja -->
    <div 
      v-if="isVisible"
      :class="['chat-bubble-button', { 'open': isOpen }]"
      @click="toggleChat"
    >
      <transition name="icon-fade" mode="out-in">
        <span v-if="isOpen" key="close">✕</span>
        <span v-else key="chat">
          <img class="chat-icon" src="@/assets/image/doggy_icon.png" alt="Chat Icon" />
        </span>
      </transition>
      <div v-if="!isOpen && hasUnreadMessages" class="notification-badge"></div>
    </div>

    <!-- Ventana del chat -->
    <transition name="chat-slide">
      <div v-if="isOpen" class="chat-window">
        <div class="chat-header">
          <div class="chat-title">
            <div class="status-indicator"></div>
            <span>Doggy - Asistente AI</span>
          </div>
          <button @click="closeChat" class="close-btn">✕</button>
        </div>
        
        <div 
          ref="messagesContainer" 
          class="chat-messages"
          v-infinite-scroll="loadMoreMessages"
          :infinite-scroll-distance="limitMessages"
          :infinite-scroll-disabled="loadingMessages || !hasMoreMessages"
          infinite-scroll-direction="top"
        >
          <!-- Indicador de carga de mensajes antiguos -->
          <div v-if="loadingMessages" class="loading-indicator">
            <div class="loading-spinner"></div>
            <span>Cargando mensajes anteriores...</span>
          </div>
          
          <div 
            v-for="message in messages" 
            :key="message.id" 
            :class="['message', message.message_type === 'assistant' ? 'bot' : 'user']"
          >
            <div class="message-avatar" v-if="message.message_type === 'assistant'">
              <span>
                <img src="@/assets/image/doggy_icon.png" alt="Chat Icon" />
              </span>
            </div>
            <div class="message-bubble">
              <div class="message-content" v-if="message.message_type === 'assistant'" v-html="formatMessage(message.message_content)"></div>
              <div class="message-content" v-else>{{ message.message_content }}</div>
              <div class="message-time">
                {{ formatTime(new Date(message.createdAt)) }}
              </div>
            </div>
            <div class="message-avatar" v-if="message.message_type === 'user'">
              <span><font-awesome-icon class="user-icon" icon="user" /></span>
            </div>
          </div>
          
          <!-- Indicador de escritura -->
          <div v-if="isTyping" class="message bot">
            <div class="message-avatar">
              <span>
                <img src="@/assets/image/doggy_icon.png" alt="Chat Icon" />
              </span>
            </div>
            <div class="message-bubble typing-bubble">
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
          
          <div ref="messagesEnd"></div>
        </div>
        
        <!-- Input del chat -->
        <div class="chat-input">
          <div class="input-container">
            <textarea
              ref="messageInput"
              v-model="inputText"
              @keydown="handleKeyDown"
              @input="autoResize"
              placeholder="Escribe tu consulta veterinaria..."
              rows="1"
              :disabled="isTyping"
            ></textarea>
            <button 
              @click="sendMessage"
              :disabled="!inputText.trim() || isTyping"
              class="send-button"
            >
              <span v-if="isTyping">⏳</span>
              <span v-else>➤</span>
            </button>
          </div>
          
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, reactive, onMounted, nextTick, watch } from 'vue'
import apiClient from '@/api/apiClient'
import { useUserStore } from '@/stores/user'

export default {
  name: 'ChatAI',
  setup() {
    // Estados reactivos
    const isOpen = ref(false)
    const isTyping = ref(false)
    const hasUnreadMessages = ref(false)
    const inputText = ref('')
    const messagesContainer = ref(null)
    const messagesEnd = ref(null)
    const messageInput = ref(null)
    const limitMessages = ref(50);

    // Lista de mensajes - inicialmente vacía hasta cargar el historial
    const messages = reactive([])
    const sessionId = ref(null)
    
    // Variables de paginación
    const currentPage = ref(1)
    const totalPages = ref(1)
    const loadingMessages = ref(false)
    const hasMoreMessages = ref(true)
    const isVisible       = ref(true)
    const userStore = ref(useUserStore());
    const user_info = ref(null);

    // Cargar historial de mensajes
    const loadChatHistory = async (page = 1, append = false) => {
      try {
        if (!append) {
          loadingMessages.value = true
        }
        
        const response = await apiClient.get(`/project-ai/chat/recent?page=${page}&limit=${limitMessages.value}`)
        
        if (response.data.ok && response.data.data.messages) {
          const newMessages = response.data.data.messages.map(msg => ({
            id: msg.id,
            message_content: msg.message_content,
            message_type: msg.message_type,
            createdAt: msg.createdAt,
            user: msg.user
          }))
          
          if (!append) {
            // Primera carga - limpiar y cargar los mensajes más recientes
            messages.length = 0
            messages.push(...newMessages)
            
            // Si no hay mensajes, agregar mensaje de bienvenida
            if (messages.length === 0) {
              const welcomeMessage = {
                id: 'welcome-' + Date.now(),
                message_content: "¡Hola! Soy tu asistente veterinario inteligente. ¿En qué puedo ayudarte hoy?",
                message_type: "assistant",
                createdAt: new Date().toISOString()
              }
              messages.push(welcomeMessage)
            }
          } else {
            // Cargar mensajes más antiguos al inicio del array
            messages.unshift(...newMessages.reverse())
          }
          
          // Guardar session_id para futuras referencias
          if (response.data.data.session_id) {
            sessionId.value = response.data.data.session_id
          }
          
          // Actualizar información de paginación
          const pagination = response.data.data.pagination
          currentPage.value = pagination.page
          totalPages.value = pagination.pages
          hasMoreMessages.value = pagination.page < pagination.pages
          
        } else {
          // Si no hay datos, establecer que no hay más mensajes
          hasMoreMessages.value = false
        }
        
      } catch (error) {
        console.error('Error al cargar historial:', error)
        hasMoreMessages.value = false
        
        if (!append && messages.length === 0) {
          // Si hay error en la primera carga, mostrar mensaje de bienvenida por defecto
          const welcomeMessage = {
            id: 'welcome-' + Date.now(),
            message_content: "¡Hola! Soy tu asistente veterinario inteligente. ¿En qué puedo ayudarte hoy?",
            message_type: "assistant",
            createdAt: new Date().toISOString()
          }
          messages.push(welcomeMessage)
        }
      } finally {
        if (!append) {
          loadingMessages.value = false
        }
      }
    }
    
    // Cargar mensajes más antiguos (para scroll infinito)
    const loadMoreMessages = async () => {
      if (loadingMessages.value || !hasMoreMessages.value) return
      
      loadingMessages.value = true
      
      // Guardar la posición de scroll atual antes de cargar mensajes
      const scrollContainer = messagesContainer.value
      const scrollHeight = scrollContainer?.scrollHeight || 0
      
      try {
        const nextPage = currentPage.value + 1
        await loadChatHistory(nextPage, true)
        
        // Mantener la posición relativa de scroll después de cargar mensajes
        await nextTick()
        if (scrollContainer) {
          const newScrollHeight = scrollContainer.scrollHeight
          const heightDifference = newScrollHeight - scrollHeight
          scrollContainer.scrollTop = scrollContainer.scrollTop + heightDifference
        }
      } catch (error) {
        console.error('Error al cargar más mensajes:', error)
      } finally {
        loadingMessages.value = false
      }
    }

    // Métodos
    const toggleChat = async () => {
      isOpen.value = !isOpen.value
      if (isOpen.value) {
        hasUnreadMessages.value = false
        
        // Cargar historial de mensajes al abrir el chat
        if (messages.length === 0) {
          await loadChatHistory()
        }
        
        nextTick(() => {
          scrollToBottom()
          messageInput.value?.focus()
        })
      }
    }

    const closeChat = () => {
      isOpen.value = false
    }

    const scrollToBottom = () => {
      if (messagesEnd.value) {
        messagesEnd.value.scrollIntoView({ behavior: 'smooth' })
      }
    }

    const formatMessage = (text) => {
      if (!text) return ''
      
      let formatted = text
      
      // Convertir texto en negrita **texto** a <strong>texto</strong>
      formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      
      // Convertir listas numeradas
      const lines = formatted.split('\n')
      let inList = false
      const processedLines = []
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim()
        
        // Detectar elementos de lista numerada
        if (/^\d+\.\s/.test(line)) {
          if (!inList) {
            processedLines.push('<ol class="chat-list">')
            inList = true
          }
          const content = line.replace(/^\d+\.\s/, '')
          processedLines.push(`<li>${content}</li>`)
        } else if (line === '' && inList) {
          // Línea vacía dentro de lista - mantener la lista
          continue
        } else {
          if (inList) {
            processedLines.push('</ol>')
            inList = false
          }
          if (line !== '') {
            processedLines.push(line)
          } else {
            processedLines.push('<br>')
          }
        }
      }
      
      // Cerrar lista si terminó con una lista
      if (inList) {
        processedLines.push('</ol>')
      }
      
      formatted = processedLines.join('\n')
      
      // Convertir saltos de línea simples a <br> (excepto los que ya están en listas)
      formatted = formatted.replace(/\n(?!<)/g, '<br>')
      
      return formatted
    }

    const formatTime = (timestamp) => {
      const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
      return date.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const sendMessage = async () => {
      if (!inputText.value.trim() || isTyping.value) return

      const messageText = inputText.value
      const userMessage = {
        id: 'temp-' + Date.now(),
        message_content: messageText,
        message_type: 'user',
        createdAt: new Date().toISOString()
      }

      messages.push(userMessage)
      inputText.value = ''
      isTyping.value = true

      await nextTick()
      scrollToBottom()

      try {
        // Enviar mensaje a la API real
        const response = await apiClient.post('/project-ai/chat/message/send-message-from-chat-heads', {
          message_content: messageText
        })
        
        if (response.data.ok && response.data.data) {
          // Reemplazar mensaje temporal del usuario con el real de la API
          const userMessageIndex = messages.findIndex(msg => msg.id === userMessage.id)
          if (userMessageIndex !== -1) {
            messages[userMessageIndex] = {
              id: response.data.data.user_message.id,
              message_content: response.data.data.user_message.message_content,
              message_type: response.data.data.user_message.message_type,
              createdAt: response.data.data.user_message.createdAt,
              session_id: response.data.data.user_message.session_id
            }
          }
          
          // Agregar respuesta del asistente
          const botMessage = {
            id: response.data.data.ai_response.id,
            message_content: response.data.data.ai_response.message_content,
            message_type: response.data.data.ai_response.message_type,
            createdAt: response.data.data.ai_response.createdAt,
            session_id: response.data.data.ai_response.session_id,
            tokens_used: response.data.data.ai_response.tokens_used,
            model_used: response.data.data.ai_response.model_used
          }
          
          messages.push(botMessage)
          
          // Actualizar session_id si no lo tenemos
          if (!sessionId.value && response.data.data.user_message.session_id) {
            sessionId.value = response.data.data.user_message.session_id
          }
          
        } else {
          // Fallback si la estructura no es la esperada
          const botMessage = {
            id: 'temp-bot-' + Date.now(),
            message_content: 'Respuesta recibida pero con estructura inesperada',
            message_type: 'assistant',
            createdAt: new Date().toISOString()
          }
          
          messages.push(botMessage)
        }
        
      } catch (error) {
        console.error('Error al enviar mensaje:', error)
        
        const errorMessage = {
          id: 'temp-error-' + Date.now(),
          message_content: error.response?.data?.message || 
                error.response?.data?.error || 
                'Lo siento, hubo un problema al procesar tu mensaje. Por favor, intenta de nuevo.',
          message_type: 'assistant',
          createdAt: new Date().toISOString()
        }
        
        messages.push(errorMessage)
      }
      
      isTyping.value = false
      
      if (!isOpen.value) {
        hasUnreadMessages.value = true
      }
      
      nextTick(() => {
        scrollToBottom()
      })
    }

    const sendSuggestion = (suggestion) => {
      inputText.value = suggestion
      sendMessage()
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        sendMessage()
      }
    }

    const autoResize = () => {
      const textarea = messageInput.value
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
      }
    }

    // Watchers
    watch(messages, (newMessages, oldMessages) => {
      // Solo hacer scroll al final si se agregaron mensajes nuevos al final de la lista
      // (no cuando se cargan mensajes antiguos al inicio)
      if (newMessages.length > oldMessages.length && !loadingMessages.value) {
        const lastNewMessage = newMessages[newMessages.length - 1]
        const lastOldMessage = oldMessages.length > 0 ? oldMessages[oldMessages.length - 1] : null
        
        // Si el último mensaje es diferente o no existía antes, hacer scroll
        if (!lastOldMessage || lastNewMessage.id !== lastOldMessage.id) {
          nextTick(() => {
            scrollToBottom()
          })
        }
      }
    }, { deep: true })

    onMounted(() => {
      user_info.value = userStore.value.user_info;
      if (user_info.value.role?.name === 'client_system_default_25cce0a1-574f') {
        isVisible.value = false;
      }
      // El historial se carga automáticamente al abrir el chat
    })

    return {
      // Estados
      isOpen,
      isTyping,
      hasUnreadMessages,
      inputText,
      messages,
      sessionId,
      loadingMessages,
      hasMoreMessages,
      currentPage,
      totalPages,
      isVisible,
      
      // Referencias
      messagesContainer,
      messagesEnd,
      messageInput,
      
      // Métodos
      toggleChat,
      closeChat,
      sendMessage,
      sendSuggestion,
      handleKeyDown,
      autoResize,
      formatTime,
      formatMessage,
      loadChatHistory,
      loadMoreMessages
    }
  }
}
</script>

<style scoped>
/* Botón flotante */
.chat-bubble-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 80px;
  height: 80px;
  background: var(--color-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  font-size: 24px;
  color: white;
  border: none;
  outline: none;
}

.chat-icon {
  width: 60px;
  height: 60px;
}

.user-icon {
  font-size: 18px;
  color: white;
}

.chat-bubble-button:hover {
  transform: scale(1.1);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
}

.chat-bubble-button.open {
  background: linear-gradient(135deg, var(--color-danger) 0%, var(--color-warning) 100%);
}

.notification-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 12px;
  height: 12px;
  background: var(--color-danger);
  border-radius: 50%;
  border: 2px solid white;
  animation: pulse-notification 2s infinite;
}

@keyframes pulse-notification {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
}

/* Transiciones */
.icon-fade-enter-active, .icon-fade-leave-active {
  transition: all 0.2s ease;
}
.icon-fade-enter-from, .icon-fade-leave-to {
  opacity: 0;
  transform: rotate(90deg);
}

.chat-slide-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.chat-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.chat-slide-enter-from, .chat-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

/* Ventana del chat */
.chat-window {
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 35rem;
  height: 39rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 999;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

/* Header */
.chat-header {
  background: var(--color-primary);
  color: white;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
}

.status-indicator {
  width: 8px;
  height: 8px;
  background: var(--color-success);
  border-radius: 50%;
  margin-right: 10px;
  animation: pulse-status 2s infinite;
}

@keyframes pulse-status {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.close-btn {
  background: transparent;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Mensajes */
.chat-messages {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f8fafc;
  scroll-behavior: smooth;
}

.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: transparent;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: var(--color-info-light);
  border-radius: 3px;
}

.message {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  max-width: 85%;
}

.message.user {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.message.bot {
  align-self: flex-start;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.message.user .message-avatar {
  background:var(--color-primary);
}

.message.bot .message-avatar {
  background: var(--color-success);
}

.message-bubble {
  display: flex;
  flex-direction: column;
}

.message-content {
  padding: 12px 16px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
  position: relative;
}

.message.user .message-content {
  background: var(--color-primary);
  color: white;
  border-bottom-right-radius: 4px;
}

.message.bot .message-content {
  background: white;
  color: var(--bulma-content-heading-color);
  border: 1px solid var(--color-info-light);
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.message-time {
  font-size: 11px;
  color: var(--color-info);
  margin-top: 4px;
  text-align: left;
}

.message.user .message-time {
  text-align: right;
}

/* Indicador de carga de mensajes */
.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: var(--color-info);
  font-size: 14px;
  gap: 8px;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--color-info-light);
  border-top: 2px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Estilos para contenido formateado */
.message-content .chat-list {
  margin: 12px 0;
  padding-left: 0;
  margin-left: 20px;
  list-style-position: outside;
  list-style-type: decimal;
}

.message-content .chat-list li {
  margin: 6px 0;
  line-height: 1.5;
  padding-left: 8px;
  margin-left: 0;
}

.message-content strong {
  font-weight: 600;
  color: inherit;
}

/* Indicador de escritura */
.typing-bubble {
  background: white !important;
  border: 1px solid var(--color-info-light) !important;
  padding: 16px !important;
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: var(--color-info);
  border-radius: 50%;
  animation: typing-bounce 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) { animation-delay: 0ms; }
.typing-indicator span:nth-child(2) { animation-delay: 200ms; }
.typing-indicator span:nth-child(3) { animation-delay: 400ms; }

@keyframes typing-bounce {
  0%, 80%, 100% { 
    opacity: 0.3;
    transform: translateY(0); 
  }
  40% { 
    opacity: 1;
    transform: translateY(-8px); 
  }
}

/* Input del chat */
.chat-input {
  padding: 20px;
  background: white;
  border-top: 1px solid var(--color-info-light);
}

.input-container {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 12px;
}

.input-container textarea {
  flex: 1;
  border: 1px solid var(--color-info-light);
  border-radius: 20px;
  padding: 12px 16px;
  font-size: 14px;
  resize: none;
  outline: none;
  max-height: 120px;
  min-height: 44px;
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.input-container textarea:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-light);
}

.input-container textarea:disabled {
  background: var(--white-10);
  color: var(--color-info);
  cursor: not-allowed;
}

.send-button {
  background: var(--color-primary);
  border: none;
  color: white;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.send-button:hover:not(:disabled) {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(6, 120, 183, 0.3);
}

.send-button:disabled {
  background: var(--color-info-light);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
/* Responsive */
@media (max-width: 480px) {
  .chat-window {
    width: calc(100vw - 20px);
    height: calc(100vh - 100px);
    bottom: 80px;
    right: 10px;
    left: 10px;
  }
  
  .chat-bubble-button {
    bottom: 15px;
    right: 15px;
    width: 56px;
    height: 56px;
  }
}

@media (max-width: 320px) {
  .chat-window {
    width: calc(100vw - 10px);
    right: 5px;
    left: 5px;
  }
}
</style>
